import Axios from 'axios';
import { type LocationQuery } from 'vue-router';
import TripModule from '../TripModule';
import {
    FlightFilters,
    type FlightFiltersData,
    FlightServiceException,
    type ForecastState,
} from './FlightModuleTypes';
import FlightAutoPriceClass from './FlightAutoPriceClass';
import { type FlightSearchForm } from '@search/trip/flight/FlightSearchForm';
import {
    AirportsApiApiAirportsNearbyGet,
    CabinClassRequest,
} from '@clients/FlightApi';
import { TaxiLateralLoader } from '@itinerary/trip/bus/TaxiLateralLoader';
import type Flight from '@itinerary/trip/flight/Flight';
import FlightPackage from '@itinerary/trip/flight/FlightPackage';
import FlightPackageSet from '@itinerary/trip/flight/FlightPackageSet';
import type FlightPriceClass from '@itinerary/trip/flight/FlightPriceClass';
import { type FlightSegment } from '@itinerary/trip/flight/FlightSegment';
import type TripSegment from '@itinerary/trip/TripSegment';
import {
    DurationFilter,
    TimeFilter,
    type TripShareInfo,
    TripSortBy,
} from '@itinerary/trip/TripModuleTypes';
import {
    CheckboxFilter,
    type Filter,
    type IdTitle,
    InputFilter,
    LoadingStatus,
    PriceFilter,
    RadioFilter,
    type SortBy,
} from '@itinerary/ItineraryModuleTypes';
import { AutoReserveType, ReserveType } from '@utils/itinerary';
import { type Location } from '@utils/types';
import { type FlightPathData, type FlightUrlParams } from '@utils/flight';
import { TripType } from '@utils/trip';
import { type FlightPackageLoaderConfig } from '@itinerary/trip/flight/types';

export default class FlightModule extends TripModule<
    Flight,
    FlightPriceClass,
    FlightFiltersData,
    Location
> {
    //#region state
    selectedPackage: FlightPackage | null = null;
    forecastPredicate = 'Unknown' as ForecastState;
    //#endregion

    override get defaultAutoReserveType() {
        return AutoReserveType.Item;
    }

    override getPackageSet(
        loaderConfig: FlightPackageLoaderConfig
    ): FlightPackageSet {
        return new FlightPackageSet(loaderConfig, this.getInitialFilters());
    }

    override get reserveInfo() {
        return this.flightForm.reserveInfo;
    }

    override get metaTagTitle(): string {
        return `خرید بلیط هواپیما ${this.originName} ${this.destinationName} با بهترین قیمت - مِستربلیط`;
    }

    override descriptionTemplate =
        'خرید اینترنتی بلیط هواپیما (مبدا) (مقصد) همراه با بلیط های لحظه آخری و برنامه پروازها. مسیر و تاریخ را مشخص کنید و سپس بلیط هواپیما (مبدا) (مقصد) را به صورت آنلاین رزرو کنید. مِستربلیط سایت و اپلیکیشن رزرو بلیط هواپیما با ارزان‌ترین قیمت و بدون نیاز به ثبت نام';

    override get canonicalRegex() {
        return /([A-Z]{3})ALL/g;
    }

    override get emptyPackage() {
        const flightForm = useFlightForm();
        const flightPackage = new FlightPackage(
            {
                Segments: [
                    {
                        ConnectionTime: '00:00:00',
                        TotalTime: '00:00:00',
                        Legs: [
                            {
                                Destination: flightForm.destination?.title,
                                Origin: flightForm.origin?.title,
                                DepartureTime:
                                    flightForm.departureDate?.toISOString(),
                                DepartureDateString:
                                    flightForm.departureDate?.toISOString(),
                            },
                        ],
                    },
                ],
                AutomaticReserve: true,
                Prices: [
                    {
                        Capacity: 0,
                    },
                ],
            },
            {
                adultCount: flightForm.adultCount,
                childCount: flightForm.childCount,
                infantCount: flightForm.infantCount,
            },
            {}
        );
        flightPackage.selectedPrice = new FlightAutoPriceClass(false);
        return flightPackage;
    }

    override get defaultSortBy(): TripSortBy {
        return TripSortBy.Price;
    }

    private get flightForm(): FlightSearchForm {
        const { reserveType } = useSearchFormStore();
        if (reserveType === ReserveType.Bundle) {
            return useBundleForm().getFlightForm();
        }
        return useFlightForm();
    }

    override applyFilter(
        pkg: FlightPackage,
        filters: Record<(typeof FlightFilters)[keyof typeof FlightFilters], any>
    ): boolean {
        if (!pkg.segments || !pkg.segments.length) {
            return false;
        }

        const departureMinutes = timeToMinutes(pkg.departureTime);
        const arrivalMinutes = timeToMinutes(pkg.arrivalTime);
        const stopCounts = pkg.segments.map(
            s =>
                s.trips.length +
                s.trips.reduce((acc, t) => acc + t.stops, 0) -
                1
        );
        const stops = ([] as string[]).concat(
            ...pkg.segments.map(s => s.trips.slice(1).map(t => t.originCode))
        );
        let result = true;
        if (filters.airline?.length) {
            result &&= filters.airline.some((a: any) =>
                pkg.segments.some(s =>
                    s.trips.some(f => f.corporationInfo.code === a)
                )
            );
        }
        if (filters.price) {
            result &&=
                pkg.singlePayable >= filters.price[0] &&
                pkg.singlePayable <= filters.price[1];
        }
        if (filters.time) {
            if (filters.time.departure?.length) {
                result &&=
                    (departureMinutes <= 8 * 60 &&
                        filters.time.departure.includes('midnight')) ||
                    (departureMinutes >= 8 * 60 &&
                        departureMinutes <= 18 * 60 &&
                        filters.time.departure.includes('day')) ||
                    (departureMinutes >= 18 * 60 &&
                        filters.time.departure.includes('night'));
            }
            if (filters.time.arrival?.length) {
                result &&=
                    (arrivalMinutes <= 8 * 60 &&
                        filters.time.arrival.includes('midnight')) ||
                    (arrivalMinutes >= 8 * 60 &&
                        arrivalMinutes <= 18 * 60 &&
                        filters.time.arrival.includes('day')) ||
                    (arrivalMinutes >= 18 * 60 &&
                        filters.time.arrival.includes('night'));
            }
        }
        if (filters.stopCount) {
            result &&= stopCounts.every(c => c <= parseInt(filters.stopCount));
        }
        if (filters.stops?.length) {
            result &&=
                !!stops.length && stops.every(s => filters.stops.includes(s));
        }
        if (filters.classOfService?.length) {
            switch (pkg.defaultPrice?.cabinClass) {
                case CabinClassRequest.Business:
                    result &&= filters.classOfService.includes('C');
                    break;
                case CabinClassRequest.EconomyPlus:
                    result &&= filters.classOfService.includes('P');
                    break;
                default:
                    result &&= filters.classOfService.includes('Y');
                    break;
            }
        }
        if (filters.duration?.departure) {
            result &&=
                Math.ceil(pkg.segments[0].duration / 3600) >=
                    filters.duration.departure[0] &&
                Math.ceil(pkg.segments[0].duration / 3600) <=
                    filters.duration.departure[1];
        }
        if (filters.duration?.return) {
            result &&=
                Math.ceil(pkg.segments[1].duration / 3600) >=
                    filters.duration.return[0] &&
                Math.ceil(pkg.segments[1].duration / 3600) <=
                    filters.duration.return[1];
        }
        if (filters.flightNumber?.trim()) {
            result &&= pkg.segments.some(seg =>
                seg.trips.some(trip =>
                    trip.flightNumber
                        .trim()
                        .startsWith(filters.flightNumber.trim())
                )
            );
        }
        if (filters.labels?.length) {
            result &&= filters.labels.some((a: string) =>
                pkg.specialOffers.some(offer => offer.Title === a)
            );
        }
        return result;
    }

    override get currentPackageSet(): FlightPackageSet | null | undefined {
        return this.itineraryPackageSets[this.currentSetIndex] as
            | FlightPackageSet
            | null
            | undefined;
    }

    get meta() {
        return (
            (this.currentPackageSet
                ?.filterMetaData as FlightFiltersData['meta']) || null
        );
    }

    override get filters(): Filter[] {
        if (!this.currentPackageSet || !this.currentPackageSet.filterMetaData) {
            return [];
        }
        const { flightForm } = this;
        const meta = this.currentPackageSet.filterMetaData;

        const stopsArr = new Array<TripSegment<Flight>>()
            .concat(
                ...this.list
                    .filter(p => p.segments)
                    .map(p => p.segments as TripSegment<Flight>[])
            )
            .filter(s => s.trips?.length > 1)
            .map(s =>
                s.trips
                    .slice(1)
                    .map(t => ({ title: t.origin, id: t.originCode }))
            );
        const stops = new Array<{ title: string; id: string }>().concat(
            ...stopsArr
        );
        const uniqueStops = stops.filter(
            s => stops.find(ss => ss.id === s.id) === s
        );
        const labelsFilterList: IdTitle[] = this.currentPackageSet.list
            .filter(pkg => pkg.specialOffers)
            .map(pkg => pkg.specialOffers)
            .map(label => label.map(label => label.Title ?? ''))
            .flat()
            .filter((value, index, array) => array.indexOf(value) === index)
            .map(item => ({ id: item, title: item }));
        const filters: Filter[] = [
            new PriceFilter(
                FlightFilters.Price,
                meta.MinPrice ?? 0,
                meta.MaxPrice ?? 0
            ),
            new CheckboxFilter(
                FlightFilters.ClassOfService,
                [
                    {
                        id: 'P',
                        title: 'اکونومی پلاس',
                    },
                    {
                        id: 'Y',
                        title: 'اکونومی',
                    },
                    {
                        id: 'C',
                        title: 'بیزینس',
                    },
                ],
                'کلاس پروازی'
            ),
            ...(flightForm.isDomestic
                ? []
                : [
                      new RadioFilter(
                          FlightFilters.StopCount,
                          [...Array(4).keys()].map(i => ({
                              id: i.toString(),
                              title: i === 0 ? 'بدون توقف' : `${i} توقف و کمتر`,
                          })),
                          'تعداد توقف'
                      ),
                  ]),
            new TimeFilter(
                FlightFilters.Time,
                flightForm.origin?.title ?? '',
                flightForm.destination?.title ?? ''
            ),
            new CheckboxFilter(
                FlightFilters.Stops,
                uniqueStops,
                'فرودگاه‌های توقف'
            ),
            new CheckboxFilter(
                FlightFilters.Airline,
                meta.Airlines?.map(a => ({
                    title: a.PersianTitle ?? '',
                    id: a.IataCode ?? '',
                })) || [],
                'شرکت هواپیمایی'
            ),
            new CheckboxFilter(
                FlightFilters.Labels,
                labelsFilterList,
                'برچسب‌ها'
            ),
        ];
        if (flightForm.isForeign) {
            const departureList = this.list
                .map(pck => pck.segments[0])
                .map(seg => seg as FlightSegment)
                .map(seg => seg.duration);
            const returnList = this.list
                .map(pck => pck.segments[1])
                .map(seg => seg as FlightSegment)
                .map(seg => seg?.duration || 0);
            const departureMin = departureList.reduce(
                (previousValue, currentValue) => {
                    if (currentValue < previousValue) {
                        return currentValue;
                    }
                    return previousValue;
                },
                Number.MAX_VALUE
            );
            const departureMax = departureList.reduce(
                (previousValue, currentValue) => {
                    if (currentValue > previousValue) {
                        return currentValue;
                    }
                    return previousValue;
                },
                0
            );
            const returnMin = returnList.reduce(
                (previousValue, currentValue) => {
                    if (currentValue < previousValue) {
                        return currentValue;
                    }
                    return previousValue;
                },
                Number.MAX_VALUE
            );
            const returnMax = returnList.reduce(
                (previousValue, currentValue) => {
                    if (currentValue > previousValue) {
                        return currentValue;
                    }
                    return previousValue;
                },
                0
            );
            filters.push(
                new DurationFilter(
                    flightForm.tripType === TripType.RoundTrip,
                    {
                        departure: Math.ceil(departureMin / 3600),
                        return:
                            flightForm.tripType === TripType.RoundTrip
                                ? Math.ceil(returnMin / 3600)
                                : undefined,
                    },
                    {
                        departure: Math.ceil(departureMax / 3600),
                        return:
                            flightForm.tripType === TripType.RoundTrip
                                ? Math.ceil(returnMax / 3600)
                                : undefined,
                    }
                )
            );
            filters.push(
                new InputFilter(FlightFilters.FlightNumber, 'شماره پرواز', {
                    placeholder: 'شماره پرواز',
                })
            );
        }
        return this.sortFilters(filters);
    }

    override get haveTracking(): boolean {
        if (!this.priceTrackEnabled) {
            return false;
        }
        if (!super.haveTracking && !this.areAllPackagesFull) {
            return true;
        } else {
            return super.haveTracking;
        }
    }

    override get lateralLoader(): TaxiLateralLoader | null {
        return new TaxiLateralLoader();
    }

    removeDiscountOnAvailableList() {
        this.itineraryPackageSets.forEach(packageSet => {
            packageSet?.list.forEach(pkg => {
                pkg.prices.forEach(p => {
                    p.classes.forEach(c => {
                        c.couponCode = null;
                    });
                });
            });
        });
        this.isCouponAppliedInList = false;
    }

    override getItineraryPackageLoaderConfigs(
        params: FlightUrlParams
    ): FlightPackageLoaderConfig[] {
        const { flightForm } = this;
        const domesticAirportMap = flightForm.domesticAirportCodesMap;
        const isForeignRoundTrip =
            params.routes.length === 1 &&
            params.routes[0].returnDate &&
            !(
                domesticAirportMap[params.routes[0].origin] &&
                domesticAirportMap[params.routes[0].destination]
            );

        // When it is a foreign round-trip, we must produce just one loader
        if (isForeignRoundTrip) {
            return [
                {
                    originCode: params.routes[0].origin,
                    destinationCode: params.routes[0].destination,
                    date: params.routes[0].departureDate,
                    adultCount: params.adultCount,
                    childCount: params.childCount,
                    infantCount: params.infantCount,
                    cabinClass: params.cabinClass,
                    returnDate: params.routes[0].returnDate,
                    url: useRoute().fullPath,
                    isRealTime: flightForm.isForeign,
                    isCapacityTrackCampaign: flightForm.isCapacityTrackCampaign,
                    token: useAuthStore().token,
                    xplayerID: useAuthStore().uuid ?? undefined,
                    totalSeatCount: 0, // fake
                },
            ];
        }

        // Otherwise, we must generate a loader for each route and each date
        return params.routes
            .map(route =>
                [
                    <FlightPackageLoaderConfig>{
                        originCode: params.routes[0].origin,
                        destinationCode: params.routes[0].destination,
                        date: params.routes[0].departureDate,
                        adultCount: params.adultCount,
                        childCount: params.childCount,
                        infantCount: params.infantCount,
                        cabinClass: params.cabinClass,
                        url: useRoute().fullPath,
                        isRealTime: flightForm.isForeign,
                        token: useAuthStore().token,
                        xplayerID: useAuthStore().uuid ?? undefined,
                        originName: flightForm.origin?.title,
                        destinationName: flightForm.destination?.title,
                        totalSeatCount: 0, // fake
                    },
                    route.returnDate
                        ? <FlightPackageLoaderConfig>{
                              originCode: route.destination,
                              destinationCode: route.origin,
                              date: route.returnDate,
                              adultCount: params.adultCount,
                              childCount: params.childCount,
                              infantCount: params.infantCount,
                              cabinClass: params.cabinClass,
                              url: useRoute().fullPath,
                              isRealTime: flightForm.isForeign,
                              token: useAuthStore().token,
                              xplayerID: useAuthStore().uuid ?? undefined,
                              originName: flightForm.destination?.title,
                              destinationName: flightForm.origin?.title,
                              totalSeatCount: 0, // fake
                          }
                        : null,
                ]
                    // Eliminate null values
                    .filter<FlightPackageLoaderConfig>(
                        Boolean as unknown as (
                            value: FlightPackageLoaderConfig | null
                        ) => value is FlightPackageLoaderConfig
                    )
            )
            .flat();
    }

    override getInitialFilters(): { [key in FlightFiltersData['key']]: any } {
        return {
            airline: null,
            duration: null,
            price: null,
            stopCount: null,
            stops: null,
            time: null,
            classOfService: [],
            labels: null,
            flightNumber: '',
        };
    }

    override async loadAvailable(params: { index: number; inForm?: boolean }) {
        const packageSet = this.itineraryPackageSets[params.index];
        // restore before load states
        this.forecastPredicate = 'Unknown';
        this.isCouponAppliedInList = false;
        // Calling the super
        await super.loadAvailable(params);
        if (packageSet && !params.inForm) {
            this.loadForecast({
                originCode: packageSet.originCode,
                destinationCode: packageSet.destinationCode,
                departureDate: packageSet.date,
            });
        }
    }

    override get dateTitle(): string {
        const { isDomestic, tripType, returnDate } = this.flightForm;
        if (!isDomestic && tripType === TripType.RoundTrip && returnDate) {
            return `${super.dateTitle} به ${dayConfig
                .js(returnDate)
                .format(formatter({ format: 'long', includeWeekday: true }))}`;
        } else {
            return super.dateTitle;
        }
    }

    override itineraryPackageSets: (FlightPackageSet | null)[] = [];

    override async loadSuggestions(params: TripShareInfo) {
        try {
            const { flightForm } = this;
            const { data } = await AirportsApiApiAirportsNearbyGet(
                {
                    originLatitude: params.origin.latitude,
                    originLongitude: params.origin.longitude,
                    destinationLatitude: params.destination.latitude,
                    destinationLongitude: params.destination.longitude,
                    departureDate: params.departureDate!.toISOString(),
                },
                {
                    ...params,
                    timeout: 10000,
                }
            );
            if (this.itineraryPackageSets?.[this.currentSetIndex]) {
                params.adultCount =
                    this.itineraryPackageSets[this.currentSetIndex]!.adultCount;
                params.childCount =
                    this.itineraryPackageSets[this.currentSetIndex]!.childCount;
                params.infantCount =
                    this.itineraryPackageSets[
                        this.currentSetIndex
                    ]!.infantCount;
            }

            flightForm.setOrigin({
                code: data[0]?.SourceId ?? '',
                title: data[0]?.SourceTitle ?? '',
            });
            flightForm.setDestination({
                code: data[0]?.DestinationId ?? '',
                title: data[0]?.DestinationTitle ?? '',
            });
            flightForm.setTripTypeAndCheckDate(params.tripType);
            const url = flightForm.generateViewUrl({
                origin: this.isSelectingReturn
                    ? data[0]?.DestinationId ?? ''
                    : data[0]?.SourceId ?? '',
                destination: this.isSelectingReturn
                    ? data[0]?.SourceId ?? ''
                    : data[0]?.DestinationId ?? '',
                departureDate: dayConfig
                    .jalali(params.departureDate)
                    .format(formatter()),
                returnDate:
                    params.tripType === TripType.RoundTrip
                        ? dayConfig
                              .jalali(params.returnDate)
                              .format(formatter())
                        : undefined,
                adultCount: params.adultCount.toString(),
                childCount: params.childCount.toString(),
                infantCount: params.infantCount.toString(),
                cabinClass: !flightForm.isDomestic
                    ? `/${flightForm.flightCabinClass?.code}`
                    : '',
            });
            this.setPackageSet({
                index: this.currentSetIndex,
                set:
                    data.length !== 0
                        ? new FlightPackageSet(
                              {
                                  originCode: data[0].SourceId ?? '',
                                  destinationCode: data[0].DestinationId ?? '',
                                  date: this.isSelectingReturn
                                      ? params.returnDate!
                                      : params.departureDate!,
                                  adultCount: params.adultCount,
                                  childCount: params.childCount,
                                  infantCount: params.infantCount,
                                  url,
                                  originName: data[0].SourceTitle ?? '',
                                  destinationName:
                                      data[0].DestinationTitle ?? '',
                                  totalSeatCount: 0, // fake
                              },
                              this.getInitialFilters(),
                              data[0].MinPrice ?? 0,
                              true
                          )
                        : null,
                totalLength: params.expectedPackagesToBeSelected,
            });
        } catch (e) {
            if (Axios.isAxiosError(e)) {
                throw new FlightServiceException(e.response?.data?.Message);
            }
            throw new FlightServiceException('');
        }
    }

    async loadForecast(params: {
        originCode: string;
        destinationCode: string;
        departureDate: Date;
    }) {
        if (
            this.flightForm.isForeign ||
            !this.currentItineraryPackages ||
            !params.departureDate
        ) {
            return;
        }

        const priceList = Object.assign(
            {},
            ...(<FlightPackage[]>this.currentItineraryPackages)
                .filter(
                    p =>
                        p.defaultPrice != null &&
                        p.defaultPrice.cabinClass !== CabinClassRequest.Business
                )
                .map(p => ({
                    [p.segments[0].trips[0].flightNumber]: {
                        Price: p.defaultPrice?.singlePayable,
                        Charter: p.segments[0].trips[0].type === 0,
                    },
                }))
        );

        try {
            const { data } = await Axios.post(
                `${useBaseUrls().forecast}/predict`,
                {
                    From: params.originCode,
                    To: params.destinationCode,
                    DepartureDate: `${params.departureDate.getFullYear()}-${(
                        params.departureDate.getMonth() + 1
                    )
                        .toString()
                        .padStart(2, '0')}-${params.departureDate
                        .getDate()
                        .toString()
                        .padStart(2, '0')}`,
                    price_list: priceList,
                }
            );

            this.forecastPredicate =
                data === 'Wait' ? 'Wait' : data === 'Buy' ? 'Buy' : 'Unknown';
        } catch (e) {
            reportException(e);
        }
    }

    get economyReservableList(): FlightPackage[] {
        return (this.sortedReservableList as FlightPackage[]).filter(
            pkg => pkg.cabinClass === CabinClassRequest.Economy
        );
    }

    override get sortByList(): SortBy[] {
        const sortList = [
            {
                type: TripSortBy.Time,
                label: 'زود‌ترین زمان',
            },
            {
                type: TripSortBy.Price,
                label: 'کمترین قیمت',
            },
        ];
        if (this.flightForm.isForeign) {
            sortList.push({
                type: TripSortBy.Duration,
                label: 'سریع‌ترین زمان',
            });
        }
        return sortList;
    }

    override async formAutoReserve(query: LocationQuery) {
        const { flightForm } = this;
        const params = await flightForm.parsePath(query as FlightPathData);
        const itineraryStore = useItineraryStore();
        const searchFormStore = useSearchFormStore();
        await flightForm.loadViewUrlParams(params);
        searchFormStore.urlParams = params;
        itineraryStore.initItineraryModule(params);
        searchFormStore.initSearchForm();
        if (this.currentLoadingStatus !== LoadingStatus.Loading) {
            itineraryStore.setPackageSetIndex(0);
            if (!this.isSetLoaded(0)) {
                await this.loadAvailable({
                    index: 0,
                    inForm: true,
                });
                if (
                    this.itineraryPackageSets[0]?.loadingStatus ===
                    LoadingStatus.Error
                ) {
                    throw new Error('در حال حاضر سرور هواپیما قطع می‌باشد.');
                }
            }
        }
        const formStore = useFormStore();
        formStore.setCounts({
            adultCount: params.adultCount,
            childCount: params.childCount,
            infantCount: params.infantCount,
        });
        const packageSelectStore = usePackageSelectStore();
        // problem here
        const returnFlight = query?.returnFlight;
        const departureFlight = query?.departureFlight;
        const departurePackage = this.sortedReservableList.find(
            ({ uniqueId }) => uniqueId === departureFlight
        );
        // leads here
        if (departurePackage) {
            this.beforeSelectPackage({
                index: 0,
                package: departurePackage,
            });
            packageSelectStore.setSelectedPackage({
                index: 0,
                package: departurePackage,
            });
        } else {
            throw new Error('پرواز یافت نشد!');
        }
        if (
            params.returnDate &&
            returnFlight &&
            this.itineraryPackageSets.length > 1
        ) {
            itineraryStore.setPackageSetIndex(1);
            if (!this.isSetLoaded(1)) {
                await this.loadAvailable({
                    index: 1,
                    inForm: true,
                });
            }
            if (
                this.itineraryPackageSets[1]?.loadingStatus ===
                LoadingStatus.Error
            ) {
                throw new Error('در حال حاضر سرور هواپیما قطع می‌باشد.');
            }
            const returnPackage = this.sortedReservableList.find(
                ({ uniqueId }) => uniqueId === returnFlight
            );
            if (returnPackage) {
                this.beforeSelectPackage({
                    index: 1,
                    package: returnPackage,
                });
                packageSelectStore.setSelectedPackage({
                    index: 1,
                    package: returnPackage,
                });
            } else {
                throw new Error('پرواز برگشت یافت نشد!');
            }
        }
        formStore.clearState();
        formStore.setItineraryPackagesAndDepartureTime(
            packageSelectStore.selectedPackages
        );
    }

    override get alternativeSuggestionTypes(): [ReserveType, ReserveType] {
        return [ReserveType.Taxi, ReserveType.Bus];
    }
}
